// =====================================================
// USERS ADMIN PAGE - Gestión de Usuarios
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Users,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Edit2,
  Power,
  Loader2,
  Save,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Mail,
  Key,
  Building2,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Copy,
  Check,
  Eye,
  EyeOff,
  Star,
} from 'lucide-react';
import { usersApi, type User, type UserLinkedSite, type Role } from '../../api/users';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { toast } from '../../components/ui/Toast';

type SortField = 'firstName' | 'lastName' | 'email' | 'createdAt';

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  role: Role;
  password: string;
}

const ROLE_LABELS: Record<Role, string> = {
  USER: 'Usuario',
  GESTOR_SEDE: 'Gestor de Sede',
  ADMIN: 'Administrador',
};

const ROLE_ICONS: Record<Role, React.ReactNode> = {
  USER: <Shield className="h-3.5 w-3.5" />,
  GESTOR_SEDE: <ShieldCheck className="h-3.5 w-3.5" />,
  ADMIN: <ShieldAlert className="h-3.5 w-3.5" />,
};

const ROLE_COLORS: Record<Role, string> = {
  USER: 'bg-gray-100 text-gray-700',
  GESTOR_SEDE: 'bg-blue-100 text-blue-700',
  ADMIN: 'bg-purple-100 text-purple-700',
};

export function UsersAdminPage() {
  // Data state
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<any>(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<Role | ''>('');
  const [sortBy, setSortBy] = useState<SortField>('firstName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Modal state - Crear/Editar
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<User | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Modal state - Sedes
  const [isSitesModalOpen, setIsSitesModalOpen] = useState(false);
  const [sitesItem, setSitesItem] = useState<User | null>(null);
  const [linkedSites, setLinkedSites] = useState<UserLinkedSite[]>([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [defaultSiteId, setDefaultSiteId] = useState<string>('');
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isSavingSites, setIsSavingSites] = useState(false);

  // Modal state - Contraseña temporal
  const [tempPasswordModal, setTempPasswordModal] = useState<{
    isOpen: boolean;
    password: string;
    userName: string;
  }>({ isOpen: false, password: '', userName: '' });
  const [copied, setCopied] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: User | null;
    action: 'activate' | 'deactivate' | 'resetPassword';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserFormData>({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: 'USER',
      password: '',
    },
  });

  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await usersApi.getAll({
        page,
        limit,
        search: searchQuery || undefined,
        sortBy,
        sortOrder,
        includeInactive: showInactive,
        role: filterRole || undefined,
      });
      setUsers(result.data);
      setMeta(result.meta);
    } catch (err: any) {
      setError(err.message || 'Error al cargar usuarios');
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, searchQuery, sortBy, sortOrder, showInactive, filterRole]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormError(null);
    setShowPassword(false);
    reset({
      email: '',
      firstName: '',
      lastName: '',
      role: 'USER',
      password: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (item: User) => {
    setEditingItem(item);
    setFormError(null);
    setShowPassword(false);
    reset({
      email: item.email,
      firstName: item.firstName,
      lastName: item.lastName,
      role: item.role,
      password: '',
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
    }
  };

  const handleSave = async (formData: UserFormData) => {
    setFormError(null);
    setIsSaving(true);

    try {
      if (editingItem) {
        await usersApi.update(editingItem.id, {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
        });
        toast.success('Actualizado', `Usuario "${formData.firstName} ${formData.lastName}" actualizado`);
      } else {
        const result = await usersApi.create({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          password: formData.password || undefined,
        });
        
        // Si se generó contraseña temporal, mostrarla
        if (result.tempPassword) {
          setTempPasswordModal({
            isOpen: true,
            password: result.tempPassword,
            userName: `${formData.firstName} ${formData.lastName}`,
          });
        } else {
          toast.success('Creado', `Usuario "${formData.firstName} ${formData.lastName}" creado`);
        }
      }
      setIsModalOpen(false);
      setEditingItem(null);
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = (item: User) => {
    setConfirmDialog({
      isOpen: true,
      item,
      action: item.isActive ? 'deactivate' : 'activate',
    });
  };

  const handleResetPassword = (item: User) => {
    setConfirmDialog({
      isOpen: true,
      item,
      action: 'resetPassword',
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await usersApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivado', `Usuario "${confirmDialog.item.firstName}" desactivado`);
      } else if (confirmDialog.action === 'activate') {
        await usersApi.reactivate(confirmDialog.item.id);
        toast.success('Activado', `Usuario "${confirmDialog.item.firstName}" activado`);
      } else if (confirmDialog.action === 'resetPassword') {
        const result = await usersApi.resetPassword(confirmDialog.item.id);
        setTempPasswordModal({
          isOpen: true,
          password: result.tempPassword,
          userName: `${confirmDialog.item.firstName} ${confirmDialog.item.lastName}`,
        });
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo completar la acción');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  // =====================================================
  // MODAL: SEDES
  // =====================================================
  const handleOpenSitesModal = async (item: User) => {
    setSitesItem(item);
    setIsSitesModalOpen(true);
    setIsLoadingSites(true);

    try {
      const data = await usersApi.getLinkedSites(item.id);
      setLinkedSites(data);

      const linked = data.filter((s) => s.isLinked);
      setSelectedSiteIds(linked.map((s) => s.id));
      
      const defaultSite = linked.find((s) => s.isDefault);
      setDefaultSiteId(defaultSite?.id || '');
    } catch (err: any) {
      toast.error('Error', 'No se pudieron cargar las sedes');
      setIsSitesModalOpen(false);
    } finally {
      setIsLoadingSites(false);
    }
  };

  const handleCloseSitesModal = () => {
    if (!isSavingSites) {
      setIsSitesModalOpen(false);
      setSitesItem(null);
      setLinkedSites([]);
      setSelectedSiteIds([]);
      setDefaultSiteId('');
    }
  };

  const handleToggleSite = (siteId: string) => {
    setSelectedSiteIds((prev) => {
      const isSelected = prev.includes(siteId);
      if (isSelected) {
        // Al quitar, también quitar como default si lo era
        if (defaultSiteId === siteId) {
          setDefaultSiteId('');
        }
        return prev.filter((id) => id !== siteId);
      } else {
        return [...prev, siteId];
      }
    });
  };

  const handleSetDefaultSite = (siteId: string) => {
    // Solo permitir si está seleccionado
    if (selectedSiteIds.includes(siteId)) {
      setDefaultSiteId(siteId === defaultSiteId ? '' : siteId);
    }
  };

  const handleSaveSites = async () => {
    if (!sitesItem) return;
    setIsSavingSites(true);
    try {
      await usersApi.updateSites(sitesItem.id, {
        siteIds: selectedSiteIds,
        defaultSiteId: defaultSiteId || undefined,
      });
      toast.success('Guardado', 'Sedes actualizadas');
      setIsSitesModalOpen(false);
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo guardar');
    } finally {
      setIsSavingSites(false);
    }
  };

  // Copiar contraseña
  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPasswordModal.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Error', 'No se pudo copiar');
    }
  };

  // Helpers
  const clearAllFilters = () => {
    setShowInactive(false);
    setSearchQuery('');
    setFilterRole('');
    setPage(1);
  };

  const hasActiveFilters = showInactive || searchQuery || filterRole;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
          <p className="text-sm text-gray-500 mt-1">Gestión de usuarios del sistema</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por nombre o email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </form>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-600'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nuevo usuario
            </button>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">
              Filtros activos:
            </span>
            {showInactive && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Incluye inactivos
                <button onClick={() => { setShowInactive(false); setPage(1); }} className="ml-1.5 hover:text-blue-900">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {filterRole && (
              <div className="inline-flex items-center bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">
                Rol: {ROLE_LABELS[filterRole]}
                <button onClick={() => { setFilterRole(''); setPage(1); }} className="ml-1.5 hover:text-purple-900">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {searchQuery && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Búsqueda: "{searchQuery}"
                <button onClick={() => { setSearchQuery(''); setPage(1); }} className="ml-1.5 hover:text-blue-900">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            <button onClick={clearAllFilters} className="text-xs text-gray-400 hover:text-red-500 font-medium ml-2 underline underline-offset-2">
              Limpiar todo
            </button>
          </div>
        )}

        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-end justify-between border-t border-gray-100 pt-3 gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <label className="inline-flex items-center cursor-pointer group pb-1">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => { setShowInactive(e.target.checked); setPage(1); }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900">Mostrar inactivos</span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Rol:</span>
                <select
                  value={filterRole}
                  onChange={(e) => { setFilterRole(e.target.value as Role | ''); setPage(1); }}
                  className="text-sm border-gray-200 rounded-lg py-1.5 pl-3 pr-8"
                >
                  <option value="">Todos</option>
                  <option value="USER">Usuario</option>
                  <option value="GESTOR_SEDE">Gestor de Sede</option>
                  <option value="ADMIN">Administrador</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as SortField);
                    setSortOrder(order as 'asc' | 'desc');
                    setPage(1);
                  }}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600"
                >
                  <option value="firstName-asc">Nombre (A-Z)</option>
                  <option value="firstName-desc">Nombre (Z-A)</option>
                  <option value="email-asc">Email (A-Z)</option>
                  <option value="createdAt-desc">Más reciente</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ver:</span>
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  {[10, 25, 50].map((n) => (
                    <button
                      key={n}
                      onClick={() => { setLimit(n); setPage(1); }}
                      className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 last:border-r-0 transition-colors ${
                        limit === n ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {users.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {hasActiveFilters ? 'Sin resultados' : 'No hay usuarios'}
            </h3>
            <p className="text-gray-500">
              {hasActiveFilters ? 'No se encontraron usuarios con ese criterio' : 'Crea tu primer usuario'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Usuario</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Rol</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Sedes</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((item) => (
                    <tr key={item.id} className={`hover:bg-gray-50 ${!item.isActive ? 'bg-gray-50/50' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                            {item.firstName[0]}{item.lastName[0]}
                          </div>
                          <div>
                            <span className={`font-medium ${item.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                              {item.firstName} {item.lastName}
                            </span>
                            {item.mustChangePass && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                                Debe cambiar contraseña
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 flex items-center gap-1">
                          <Mail className="h-3.5 w-3.5 text-gray-400" />
                          {item.email}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[item.role]}`}>
                          {ROLE_ICONS[item.role]}
                          {ROLE_LABELS[item.role]}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {item.sitesCount > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                            <Building2 className="h-3 w-3" />
                            {item.sitesCount}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Sin asignar</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleToggleStatus(item)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                            item.isActive ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                          role="switch"
                          aria-checked={item.isActive}
                        >
                          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                            item.isActive ? 'translate-x-5' : 'translate-x-0'
                          }`}>
                            <Power className={`h-3 w-3 absolute top-1 left-1 ${item.isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                          </span>
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleOpenSitesModal(item)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                            title="Asignar sedes"
                          >
                            <Building2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleResetPassword(item)}
                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                            title="Resetear contraseña"
                          >
                            <Key className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                            title="Editar"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {isLoading && (
              <div className="flex items-center justify-center py-8 border-t">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            )}

            {meta && meta.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {(meta.page - 1) * meta.limit + 1} a {Math.min(meta.page * meta.limit, meta.total)} de {meta.total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(meta.page - 1)}
                    disabled={!meta.hasPrevPage || isLoading}
                    className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700">Página {meta.page} de {meta.totalPages}</span>
                  <button
                    onClick={() => setPage(meta.page + 1)}
                    disabled={!meta.hasNextPage || isLoading}
                    className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL: CREAR/EDITAR USUARIO */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingItem ? 'Editar usuario' : 'Nuevo usuario'}
        subtitle={editingItem ? `Editando: ${editingItem.firstName} ${editingItem.lastName}` : 'Completa los datos del usuario'}
      >
        <form onSubmit={handleSubmit(handleSave)}>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-600">{formError}</p>
              </div>
              <button type="button" onClick={() => setFormError(null)} className="text-red-400 hover:text-red-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  {...register('firstName', { required: 'El nombre es obligatorio' })}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm ${errors.firstName ? 'border-red-300' : 'border-gray-200'}`}
                  placeholder="Nombre"
                />
                {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apellidos *</label>
                <input
                  type="text"
                  {...register('lastName', { required: 'Los apellidos son obligatorios' })}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm ${errors.lastName ? 'border-red-300' : 'border-gray-200'}`}
                  placeholder="Apellidos"
                />
                {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName.message}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                {...register('email', { required: 'El email es obligatorio' })}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm ${errors.email ? 'border-red-300' : 'border-gray-200'}`}
                placeholder="usuario@empresa.com"
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rol *</label>
              <select
                {...register('role', { required: 'El rol es obligatorio' })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
              >
                <option value="USER">Usuario</option>
                <option value="GESTOR_SEDE">Gestor de Sede</option>
                <option value="ADMIN">Administrador</option>
              </select>
            </div>

            {!editingItem && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña
                  <span className="text-gray-400 font-normal ml-1">(opcional - se genera automáticamente)</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', {
                      minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                        message: 'Debe contener mayúscula, minúscula y número',
                      },
                    })}
                    className={`w-full px-4 py-2.5 border rounded-xl text-sm pr-10 ${errors.password ? 'border-red-300' : 'border-gray-200'}`}
                    placeholder="Dejar vacío para generar automáticamente"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
              </div>
            )}
          </div>

          <ModalFooter>
            <button
              type="button"
              onClick={handleCloseModal}
              disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {editingItem ? 'Guardar' : 'Crear'}
                </>
              )}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* MODAL: SEDES */}
      <Modal
        isOpen={isSitesModalOpen}
        onClose={handleCloseSitesModal}
        title="Asignar sedes"
        subtitle={sitesItem ? `${sitesItem.firstName} ${sitesItem.lastName}` : ''}
      >
        {isLoadingSites ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-3 bg-green-50 rounded-xl border border-green-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-900">Sedes disponibles</p>
                  <p className="text-xs text-green-600">Marca las sedes a las que tendrá acceso el usuario</p>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="max-h-[300px] overflow-y-auto divide-y divide-gray-100">
                {linkedSites.map((site) => {
                  const isSelected = selectedSiteIds.includes(site.id);
                  const isDefault = defaultSiteId === site.id;

                  return (
                    <div
                      key={site.id}
                      className={`flex items-center justify-between px-4 py-3 ${isSelected ? 'bg-green-50/50' : 'bg-white'}`}
                    >
                      <label className="flex items-center gap-3 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSite(site.id)}
                          className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {site.code}
                        </span>
                        <span className={`text-sm ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                          {site.name}
                        </span>
                      </label>

                      {isSelected && (
                        <button
                          type="button"
                          onClick={() => handleSetDefaultSite(site.id)}
                          className={`p-1.5 rounded transition-colors ${
                            isDefault
                              ? 'text-amber-500 bg-amber-50'
                              : 'text-gray-300 hover:text-amber-500 hover:bg-amber-50'
                          }`}
                          title={isDefault ? 'Sede por defecto' : 'Establecer como sede por defecto'}
                        >
                          <Star className={`h-4 w-4 ${isDefault ? 'fill-current' : ''}`} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {selectedSiteIds.length > 0 && (
              <p className="text-xs text-gray-500 px-1">
                <span className="font-medium text-gray-700">{selectedSiteIds.length}</span> sede{selectedSiteIds.length !== 1 ? 's' : ''} seleccionada{selectedSiteIds.length !== 1 ? 's' : ''}
                {defaultSiteId && (
                  <span className="ml-2 text-amber-600">
                    • Sede por defecto: {linkedSites.find((s) => s.id === defaultSiteId)?.name}
                  </span>
                )}
              </p>
            )}
          </div>
        )}

        <ModalFooter>
          <button
            type="button"
            onClick={handleCloseSitesModal}
            disabled={isSavingSites}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Cancelar
          </button>
          <button
            onClick={handleSaveSites}
            disabled={isSavingSites || isLoadingSites}
            className="px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
          >
            {isSavingSites ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Guardar
              </>
            )}
          </button>
        </ModalFooter>
      </Modal>

      {/* MODAL: CONTRASEÑA TEMPORAL */}
      <Modal
        isOpen={tempPasswordModal.isOpen}
        onClose={() => setTempPasswordModal({ isOpen: false, password: '', userName: '' })}
        title="Contraseña temporal generada"
        subtitle={`Para: ${tempPasswordModal.userName}`}
      >
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <Key className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Importante</p>
                <p className="text-sm text-amber-700 mt-1">
                  Guarda esta contraseña. El usuario deberá cambiarla en su primer inicio de sesión.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 px-4 py-3 bg-gray-100 rounded-xl font-mono text-lg text-center tracking-wider">
              {tempPasswordModal.password}
            </div>
            <button
              onClick={handleCopyPassword}
              className={`p-3 rounded-xl transition-colors ${
                copied ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title="Copiar"
            >
              {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <ModalFooter>
          <button
            onClick={() => setTempPasswordModal({ isOpen: false, password: '', userName: '' })}
            className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl flex items-center gap-2"
          >
            <Check className="h-4 w-4" />
            Entendido
          </button>
        </ModalFooter>
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => !isProcessing && setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })}
        onConfirm={handleConfirmAction}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={
          confirmDialog.action === 'deactivate'
            ? '¿Desactivar usuario?'
            : confirmDialog.action === 'activate'
            ? '¿Activar usuario?'
            : '¿Resetear contraseña?'
        }
        message={
          confirmDialog.action === 'deactivate'
            ? `El usuario "${confirmDialog.item?.firstName} ${confirmDialog.item?.lastName}" será desactivado y no podrá acceder al sistema.`
            : confirmDialog.action === 'activate'
            ? `El usuario "${confirmDialog.item?.firstName} ${confirmDialog.item?.lastName}" será reactivado.`
            : `Se generará una nueva contraseña temporal para "${confirmDialog.item?.firstName} ${confirmDialog.item?.lastName}".`
        }
        confirmText={
          confirmDialog.action === 'deactivate' ? 'Desactivar' : confirmDialog.action === 'activate' ? 'Activar' : 'Resetear'
        }
      />
    </div>
  );
}

export default UsersAdminPage;
